import React, { useState } from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Stack,
  Typography,
} from '@mui/material';

import KeyboardArrowDownIcon from '../../../assets/icons/KeyboardArrowDownIcon';
import PaginatedListWrapper from '../../../components/PagedFilterList';
import { BLACK, PRIMARY_COLOR } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';

import WorkerTimecard from './WorkerTimecard';

const WorkerTimecardSet = ({
  candidateTimecards,
  total,
  isLoading,
  isDisputedTimecardUpdating,
  currentPage,
  pageSize,
  onPageChange,
  candidateCheckedMap,
  setCandidateCheckedMap,
}) => {
  const [expandedItems, setExpandedItems] = useState([]);
  const allIds = (candidateTimecards || []).map((t) => t.candidate._id).filter(Boolean);

  const handleOpenAllToggle = () => {
    setExpandedItems((prev) => (prev.length === candidateTimecards.length ? [] : allIds));
  };

  const handleAccordionChange = (id) => (_, isExpanded) => {
    setExpandedItems((prev) =>
      isExpanded ? [...prev, id] : prev.filter((itemId) => itemId !== id)
    );
  };

  const handleCandidateCheck = (candidateId, timecards, checked) => {
    const updatedTimecardMap = { ...candidateCheckedMap };

    timecards.forEach((day) => {
      const key = `${candidateId}_${day.placementId}`;
      updatedTimecardMap[key] = checked;
    });

    setCandidateCheckedMap((prev) => ({
      ...prev,
      ...updatedTimecardMap,
      [candidateId]: checked,
    }));
  };

  const handleTimecardCheck = (candidateId, key, checked, timecards) => {
    const updatedTimecardMap = {
      ...candidateCheckedMap,
      [key]: checked,
    };

    const allChecked = timecards.every(
      (day) => updatedTimecardMap[`${candidateId}_${day.placementId}`]
    );

    setCandidateCheckedMap((prev) => ({
      ...prev,
      ...updatedTimecardMap,
      [candidateId]: allChecked,
    }));
  };

  const isAllOpen = expandedItems?.length === candidateTimecards?.length;

  const accordions = candidateTimecards?.map((candidateTimecard) => {
    const id = candidateTimecard.candidate._id;
    return (
      <Accordion
        key={id}
        expanded={expandedItems.includes(id)}
        onChange={handleAccordionChange(id)}
      >
        <AccordionSummary
          aria-controls="department-content"
          id="department-header"
          expandIcon={<KeyboardArrowDownIcon />}
          sx={{ background: BLACK[10] }}
        >
          <Box display="flex" alignItems="center" flexGrow={1}>
            <Typography
              style={{
                color: PRIMARY_COLOR[70],
                fontFamily: PRIMARY_FONT[500],
                fontWeight: '600',
                marginRight: '10px',
                fontSize: '24px',
              }}
            >
              <Checkbox
                checked={candidateCheckedMap[candidateTimecard?.candidate?._id] ?? false}
                onChange={(e) =>
                  handleCandidateCheck(
                    candidateTimecard?.candidate?._id,
                    candidateTimecard.timecards,
                    e.target.checked
                  )
                }
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
              />
              {candidateTimecard?.candidate?.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Typography
              sx={{
                fontFamily: PRIMARY_FONT[400],
                alignSelf: 'center',
                fontSize: '16px',
                marginRight: '12px',
              }}
            >
              {LanguageConverterV2('timekeeping.workerTimecard.otHours')}:
              <span style={{ fontFamily: PRIMARY_FONT[500] }}>
                {candidateTimecard?.overtimeHours?.toFixed(2)}
              </span>
            </Typography>
            <Typography
              sx={{
                fontFamily: PRIMARY_FONT[400],
                alignSelf: 'center',
                fontSize: '16px',
                marginRight: '12px',
              }}
            >
              {LanguageConverterV2('timekeeping.overviewCard.totalHours')}:
              <span style={{ fontFamily: PRIMARY_FONT[500] }}>
                {candidateTimecard?.totalHours?.toFixed(2)}
              </span>
            </Typography>
            <Typography
              sx={{
                fontFamily: PRIMARY_FONT[400],
                alignSelf: 'center',
                fontSize: '14px',
                textTransform: 'uppercase',
                marginRight: '12px',
              }}
            >
              {LanguageConverterV2('button.close')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack gap="16px">
            {expandedItems.includes(id) &&
              candidateTimecard?.timecards.map((day) => {
                const key = `${candidateTimecard.candidate._id}_${day.placementId}`;
                return (
                  <WorkerTimecard
                    key={day.placementId}
                    isChecked={candidateCheckedMap[key] ?? false}
                    onTimecardChecked={(checked) =>
                      handleTimecardCheck(
                        candidateTimecard.candidate._id,
                        key,
                        checked,
                        candidateTimecard.timecards
                      )
                    }
                    weekday={day.start}
                    jobName={day.jobName}
                    candidateName={candidateTimecard?.candidate?.name}
                    shiftStart={day.start}
                    shiftEnd={day.end}
                    timecard={day.timecard}
                    gps={day?.gps}
                    attendance={day?.attendance}
                    placementId={day?.placementId}
                    corporationId={day?.corporation?._id}
                    jobId={day?.job?.jobId}
                    isDisputedTimecardUpdating={isDisputedTimecardUpdating}
                  />
                );
              })}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  });

  const content = (
    <Stack width="100%">
      {candidateTimecards && (
        <Box display="flex" justifyContent="end">
          <Typography
            onClick={handleOpenAllToggle}
            sx={{
              width: '112px',
              border: '1px solid #ccc',
              padding: '6px 24px',
              borderTopLeftRadius: '5px',
              borderTopRightRadius: '5px',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            {isAllOpen
              ? LanguageConverterV2('timekeeping.timecardList.closeAll')
              : LanguageConverterV2('timekeeping.timecardList.openAll')}
          </Typography>
        </Box>
      )}
      <Stack gap="12px">{accordions}</Stack>
    </Stack>
  );

  return (
    <PaginatedListWrapper
      isLoading={isLoading}
      content={content}
      limit={pageSize}
      currentPageNum={currentPage}
      totalListCount={total}
      handleOnPageChange={onPageChange}
    />
  );
};

WorkerTimecardSet.propTypes = {
  candidateTimecards: PropTypes.arrayOf(PropTypes.shape({})),
  total: PropTypes.number,
  isLoading: PropTypes.bool,
  isDisputedTimecardUpdating: PropTypes.bool,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func,
  candidateCheckedMap: PropTypes.shape({}),
  setCandidateCheckedMap: PropTypes.func,
};

export default WorkerTimecardSet;
